import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { Stethoscope, Languages, Eye, EyeOff, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { loginSuccess } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hostInfo, setHostInfo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/host-info");
        setHostInfo(data);
      } catch { /* host-info is a UX nicety; ignore failure */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginSuccess(data);
      if (data?.user?.must_change_password) nav("/change-password");
      else if (data.user.role === "super_admin") nav("/platform/tenants");
      else nav("/app");
    } catch (err) {
      toast.error(apiError(err, t("auth.loginError")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel (brand) */}
      <div className="relative hidden lg:flex bg-brand overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("https://images.pexels.com/photos/19963124/pexels-photo-19963124.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand/70 via-brand/60 to-brand/90" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="MediFlow Africa"
              className="w-14 h-14 rounded-2xl object-cover bg-[#3a3a3a] shrink-0 shadow-lg"
            />
            <span className="font-heading text-xl font-bold">MediFlow Africa</span>
          </div>
          <div className="max-w-md">
            <h2 className="font-heading text-4xl font-extrabold leading-tight">
              {lang === "fr"
                ? "Le pouls numérique de votre clinique."
                : "Your clinic's digital heartbeat."}
            </h2>
            <p className="mt-4 text-white/80 text-lg">
              {lang === "fr"
                ? "Patients, rendez-vous, consultations, facturation, stock médical — tout au même endroit, conçu pour l'Afrique."
                : "Patients, appointments, consultations, billing, medical stock — all in one place, built for Africa."}
            </p>
          </div>
          <div className="text-white/60 text-sm">© {new Date().getFullYear()} MediFlow Africa</div>
        </div>
      </div>

      {/* Right panel (form) */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[var(--bg-default)]">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3 lg:hidden">
              <img
                src="/logo.png"
                alt="MediFlow Africa"
                className="w-11 h-11 rounded-xl object-cover bg-[#3a3a3a] shrink-0"
              />
              <span className="font-heading text-lg font-bold text-ink">MediFlow Africa</span>
            </div>
            <button
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              data-testid="auth-lang-toggle"
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-subtle text-ink text-sm font-semibold ml-auto"
            >
              <Languages className="w-4 h-4" />
              {lang === "fr" ? "FR → EN" : "EN → FR"}
            </button>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-ink">
            {t("auth.loginTitle")}
          </h1>
          <p className="mt-2 text-ink-muted">{t("auth.loginSubtitle")}</p>

          {hostInfo?.tenant && (
            <div className="mt-5 p-4 rounded-2xl bg-brand/5 border border-brand/20 flex items-center gap-3" data-testid="tenant-brand">
              <div className="w-11 h-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center font-heading font-bold text-lg">
                {hostInfo.tenant.name?.[0] || "?"}
              </div>
              <div className="min-w-0">
                <div className="text-[11px] text-ink-muted uppercase tracking-wider">Connexion à votre clinique</div>
                <div className="font-heading font-bold text-ink truncate flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-brand" />
                  {hostInfo.tenant.name}
                </div>
              </div>
            </div>
          )}
          {hostInfo?.admin_only && (
            <div className="mt-5 p-4 rounded-2xl bg-[#0b1721] text-white flex items-center gap-3" data-testid="admin-brand">
              <ShieldCheck className="w-6 h-6" />
              <div>
                <div className="text-[11px] text-white/60 uppercase tracking-wider">Administration</div>
                <div className="font-heading font-bold">Console Super Admin</div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block mb-2 text-sm font-semibold text-ink">
                {t("auth.email")}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-14 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                placeholder="docteur@clinique.sn"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-ink">
                {t("auth.password")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full rounded-xl border border-[var(--border-default)] bg-white px-4 pr-12 h-14 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-password-toggle"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  title={showPassword ? "Masquer" : "Afficher"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg text-ink-muted hover:text-brand hover:bg-subtle transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-14 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold text-base transition-colors disabled:opacity-60 active:scale-[0.98]"
            >
              {loading ? t("common.loading") : t("auth.signIn")}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
